import React, { useEffect, useState } from 'react';
import { ShieldCheck, Award, Clock, ChevronRight, Download, Star, Lock, ExternalLink } from '../components/CustomIcons';
import { useAuth } from '../context/AuthContext';

const TABS = ['All', 'Earned', 'In Progress', 'Locked'];

const DEMO_CREDENTIALS = [
  { id: 'c1', name: 'NCCER Core Curriculum', issuer: 'NCCER', status: 'earned', issued: '2024-02-14', type: 'certification', blockchain: true, skills: ['Safety', 'Math for Trades', 'Blueprint Reading'], progress: 100 },
  { id: 'c2', name: 'Electrical Level 1', issuer: 'NCCER', status: 'in_progress', issued: null, type: 'certification', blockchain: false, skills: ['AC/DC Theory', 'Conduit Bending', 'Wire Sizing'], progress: 62 },
  { id: 'c3', name: 'OSHA 10 — Construction', issuer: 'OSHA', status: 'earned', issued: '2024-01-08', type: 'license', blockchain: true, skills: ['Hazard Recognition', 'PPE', 'Fall Protection'], progress: 100 },
  { id: 'c4', name: 'EPA 608 Certification', issuer: 'EPA', status: 'locked', issued: null, type: 'certification', blockchain: false, skills: ['Refrigerants', 'Recovery Equipment', 'Leak Detection'], progress: 0 },
  { id: 'c5', name: 'Peer Mentor Badge', issuer: 'AssistivOS', status: 'earned', issued: '2024-03-01', type: 'badge', blockchain: false, skills: ['Communication', 'Leadership', 'Empathy'], progress: 100 },
  { id: 'c6', name: 'NCCER Electrical Level 2', issuer: 'NCCER', status: 'locked', issued: null, type: 'certification', blockchain: false, skills: ['Motor Controls', 'Transformers', 'Code'], progress: 0 },
];

const STATUS_CONFIG = {
  earned:      { label: 'Earned',      bg: 'var(--green-subtle)',   color: 'var(--green-text)' },
  in_progress: { label: 'In Progress', bg: 'var(--orange-subtle)',  color: 'var(--orange-text)' },
  locked:      { label: 'Locked',      bg: 'var(--border-subtle)',  color: 'var(--text-muted)' },
};

function StatCard({ value, label, icon: Icon, color }) {
  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Icon size={15} color={color} />
        <span className="text-section-lbl">{label}</span>
      </div>
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-headline)', lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export default function Credentials() {
  const { user, authFetch } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [tab, setTab] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/gamification/credentials')
      .then(r => setCredentials(r.data?.credentials?.length ? r.data.credentials : DEMO_CREDENTIALS))
      .catch(() => setCredentials(DEMO_CREDENTIALS))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const earned      = credentials.filter(c => c.status === 'earned');
  const inProgress  = credentials.filter(c => c.status === 'in_progress');
  const locked      = credentials.filter(c => c.status === 'locked');

  const filtered = tab === 'All' ? credentials
    : tab === 'Earned' ? earned
    : tab === 'In Progress' ? inProgress
    : locked;

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div style={{ padding: '28px', background: 'var(--page-bg)', minHeight: '100vh' }} data-testid="credentials-page">
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: 'var(--text-headline)', margin: '0 0 4px 0' }}>Skills Passport</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-body)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
            Every credential earned here is verifiable by employers, courts, and institutions.
          </p>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '14px', marginBottom: '24px' }}>
          <StatCard value={earned.length}       label="Earned"      icon={ShieldCheck} color="var(--green)"  />
          <StatCard value={inProgress.length}   label="In Progress" icon={Clock}        color="var(--orange)" />
          <StatCard value={locked.length}        label="Unlockable"  icon={Lock}         color="var(--text-muted)" />
          <StatCard value={earned.filter(c => c.blockchain).length} label="Verified" icon={Star} color="#22C55E" />
        </div>

        {/* Tab bar */}
        <div className="tab-bar" style={{ marginBottom: '20px' }}>
          {TABS.map(t => (
            <button key={t} className={`tab-item${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}
              data-testid={`credentials-tab-${t.toLowerCase().replace(/\s/g,'-')}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Credential list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(c => {
            const st = STATUS_CONFIG[c.status] || STATUS_CONFIG.locked;
            return (
              <div key={c.id} className="card" style={{ padding: '18px 20px', display: 'flex', gap: '16px', alignItems: 'flex-start', opacity: c.status === 'locked' ? 0.6 : 1 }}
                data-testid={`credential-item-${c.id}`}>
                {/* Icon */}
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: c.status === 'locked' ? 'var(--border-subtle)' : 'var(--orange-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {c.status === 'locked' ? <Lock size={18} color="var(--text-muted)" /> : c.type === 'badge' ? <Award size={18} color="var(--orange)" /> : <ShieldCheck size={18} color="var(--orange)" />}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-headline)' }}>{c.name}</span>
                    <span style={{ background: st.bg, color: st.color, borderRadius: '5px', padding: '2px 7px', fontSize: '10px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '0.04em' }}>{st.label}</span>
                    {c.blockchain && <span style={{ background: 'rgba(34,197,94,0.08)', color: '#15803D', borderRadius: '5px', padding: '2px 7px', fontSize: '10px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Verified Credential</span>}
                  </div>
                  <p className="text-meta" style={{ margin: '0 0 8px 0' }}>
                    Issued by {c.issuer}{c.issued ? ` · ${new Date(c.issued).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}
                  </p>
                  {/* Skills */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(c.skills || []).map(s => (
                      <span key={s} style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', color: 'var(--text-body)', fontFamily: 'Inter, sans-serif' }}>{s}</span>
                    ))}
                  </div>
                  {/* Progress bar for in-progress */}
                  {c.status === 'in_progress' && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span className="text-meta">Progress</span>
                        <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600 }}>{c.progress}%</span>
                      </div>
                      <div className="progress-track"><div className="progress-fill" style={{ width: `${c.progress}%` }} /></div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {c.status === 'earned' && (
                    <>
                      <button style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', color: 'var(--text-body)', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', minHeight: 'var(--touch-min)' }}
                        data-testid={`download-credential-${c.id}`} aria-label={`Download ${c.name}`}>
                        <Download size={13} /> Download
                      </button>
                      {c.blockchain && (
                        <button style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', color: '#7C3AED', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', minHeight: 'var(--touch-min)' }}
                          aria-label={`Verify ${c.name}`}>
                          <ExternalLink size={13} /> Verify
                        </button>
                      )}
                    </>
                  )}
                  {c.status === 'in_progress' && (
                    <a href={`/learn`} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--orange)', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', color: '#fff', textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, minHeight: 'var(--touch-min)' }}>
                      Continue <ChevronRight size={12} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
